<template>
  <div class="multiple-invoice-print" ref="multipleInvoiceContent">
    <div class="print-header">
      <h1>HALOS Systems - Invoice Batch</h1>
      <p class="batch-info">
        Generated on {{ formatDate(new Date()) }} |
        Total Invoices: {{ invoices.length }} |
        Total Amount: ${{ totalAmount.toFixed(2) }}
      </p>
    </div>

    <div class="invoices-container">
      <div
        v-for="(invoice, index) in invoices"
        :key="invoice.INVOICE_ID"
        class="invoice-page"
        :class="{ 'page-break': index > 0 }"
      >
        <div class="invoice-header">
          <div class="company-info">
            <h1 class="company-name">HALOS Systems</h1>
            <p class="company-address">
              123 Healthcare Street<br>
              Medical City, MC 12345<br>
              Phone: (555) 123-4567<br>
              Email: billing@halossystems.com
            </p>
          </div>
          <div class="invoice-title">
            <h2>INVOICE</h2>
            <div class="invoice-number">#{{ invoice.INVOICE_ID }}</div>
          </div>
        </div>

        <div class="invoice-details">
          <div class="billing-info">
            <h3>Bill To:</h3>
            <div class="member-info">
              <p><strong>{{ invoice.MEMBER_NAME }}</strong></p>
              <p>Member ID: {{ invoice.MEMBER_ID }}</p>
              <p>Date of Birth: {{ formatDate(invoice.MEMBER_DOB) }}</p>
            </div>
          </div>

          <div class="invoice-meta">
            <div class="meta-item">
              <label>Service Date:</label>
              <span>{{ formatDate(invoice.SERVICE_DATE) }}</span>
            </div>
            <div class="meta-item">
              <label>Invoice Date:</label>
              <span>{{ formatDate(invoice.CREATED_DATE) }}</span>
            </div>
            <div class="meta-item">
              <label>Due Date:</label>
              <span>{{ formatDate(invoice.DUE_DATE) }}</span>
            </div>
            <div class="meta-item">
              <label>Status:</label>
              <span class="status-badge" :class="'status-' + invoice.STATUS.toLowerCase()">
                {{ invoice.STATUS }}
              </span>
            </div>
          </div>
        </div>

        <div class="service-details">
          <h3>Service Details</h3>
          <div class="service-description">
            {{ invoice.SERVICE_DESCRIPTION }}
          </div>
        </div>

        <div class="amount-section">
          <div class="amount-details">
            <div class="amount-row">
              <span class="label">Amount Due:</span>
              <span class="amount">${{ invoice.AMOUNT.toFixed(2) }}</span>
            </div>
            <div class="amount-row total">
              <span class="label">Total:</span>
              <span class="amount">${{ invoice.AMOUNT.toFixed(2) }}</span>
            </div>
          </div>
        </div>

        <div class="payment-info" v-if="invoice.STATUS === 'Paid'">
          <h3>Payment Information</h3>
          <div class="payment-details">
            <p v-if="invoice.PAID_DATE"><strong>Paid Date:</strong> {{ formatDate(invoice.PAID_DATE) }}</p>
            <p v-if="invoice.PAYMENT_METHOD"><strong>Payment Method:</strong> {{ invoice.PAYMENT_METHOD }}</p>
            <p v-if="invoice.REFERENCE_NUMBER"><strong>Reference Number:</strong> {{ invoice.REFERENCE_NUMBER }}</p>
          </div>
        </div>

        <div class="notes-section" v-if="invoice.NOTES">
          <h3>Notes</h3>
          <p>{{ invoice.NOTES }}</p>
        </div>

        <div class="footer">
          <div class="footer-content">
            <p><strong>Thank you for your business!</strong></p>
            <p>For questions about this invoice, please contact our billing department.</p>
            <p class="footer-small">Generated on {{ formatDate(new Date()) }} by {{ invoice.CREATED_BY }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="batch-summary">
      <h2>Batch Summary</h2>
      <div class="summary-grid">
        <div class="summary-item">
          <label>Total Invoices:</label>
          <span>{{ invoices.length }}</span>
        </div>
        <div class="summary-item">
          <label>Total Amount:</label>
          <span>${{ totalAmount.toFixed(2) }}</span>
        </div>
        <div class="summary-item">
          <label>Pending:</label>
          <span>{{ statusCounts.Pending || 0 }}</span>
        </div>
        <div class="summary-item">
          <label>Paid:</label>
          <span>{{ statusCounts.Paid || 0 }}</span>
        </div>
        <div class="summary-item">
          <label>Overdue:</label>
          <span>{{ statusCounts.Overdue || 0 }}</span>
        </div>
        <div class="summary-item">
          <label>Cancelled:</label>
          <span>{{ statusCounts.Cancelled || 0 }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MultipleInvoicePrint',
  props: {
    invoices: {
      type: Array,
      required: true
    }
  },
  computed: {
    totalAmount() {
      return this.invoices.reduce((sum, invoice) => sum + invoice.AMOUNT, 0);
    },
    statusCounts() {
      return this.invoices.reduce((counts, invoice) => {
        counts[invoice.STATUS] = (counts[invoice.STATUS] || 0) + 1;
        return counts;
      }, {});
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    print() {
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      const printContent = this.$refs.multipleInvoiceContent.innerHTML;

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Invoice Batch - ${this.invoices.length} invoices</title>
          <style>
            ${this.getPrintStyles()}
          </style>
        </head>
        <body>
          ${printContent}
        </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    },

    getPrintStyles() {
      return `
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: Arial, sans-serif;
          font-size: 12px;
          line-height: 1.4;
          color: #333;
          background: white;
        }

        .multiple-invoice-print {
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
          background: white;
        }

        .print-header {
          text-align: center;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 3px solid #00ADB5;
        }

        .print-header h1 {
          font-size: 28px;
          color: #00ADB5;
          margin-bottom: 10px;
        }

        .batch-info {
          font-size: 14px;
          color: #666;
          font-weight: bold;
        }

        .invoice-page {
          margin-bottom: 40px;
          page-break-inside: avoid;
        }

        .page-break {
          page-break-before: always;
        }

        .invoice-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 2px solid #00ADB5;
        }

        .company-name {
          font-size: 24px;
          font-weight: bold;
          color: #00ADB5;
          margin-bottom: 10px;
        }

        .company-address {
          font-size: 11px;
          color: #666;
          line-height: 1.3;
        }

        .invoice-title {
          text-align: right;
        }

        .invoice-title h2 {
          font-size: 28px;
          font-weight: bold;
          color: #333;
          margin-bottom: 5px;
        }

        .invoice-number {
          font-size: 16px;
          font-weight: bold;
          color: #00ADB5;
        }

        .invoice-details {
          display: flex;
          justify-content: space-between;
          margin-bottom: 30px;
        }

        .billing-info h3 {
          font-size: 14px;
          font-weight: bold;
          margin-bottom: 10px;
          color: #333;
        }

        .member-info p {
          margin-bottom: 5px;
          font-size: 12px;
        }

        .invoice-meta {
          text-align: right;
        }

        .meta-item {
          margin-bottom: 8px;
          font-size: 12px;
        }

        .meta-item label {
          font-weight: bold;
          margin-right: 10px;
        }

        .status-badge {
          padding: 2px 8px;
          border-radius: 3px;
          font-size: 10px;
          font-weight: bold;
          text-transform: uppercase;
        }

        .status-pending { background: #ff9800; color: white; }
        .status-paid { background: #4caf50; color: white; }
        .status-overdue { background: #f44336; color: white; }
        .status-cancelled { background: #9e9e9e; color: white; }

        .service-details {
          margin-bottom: 30px;
        }

        .service-details h3 {
          font-size: 14px;
          font-weight: bold;
          margin-bottom: 10px;
          color: #333;
        }

        .service-description {
          background: #f8f9fa;
          padding: 15px;
          border-left: 4px solid #00ADB5;
          font-size: 12px;
          line-height: 1.5;
        }

        .amount-section {
          margin-bottom: 30px;
        }

        .amount-details {
          border-top: 1px solid #ddd;
          padding-top: 15px;
        }

        .amount-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 12px;
        }

        .amount-row.total {
          font-size: 16px;
          font-weight: bold;
          border-top: 2px solid #00ADB5;
          padding-top: 10px;
          margin-top: 10px;
        }

        .amount {
          font-weight: bold;
          color: #00ADB5;
        }

        .payment-info, .notes-section {
          margin-bottom: 20px;
        }

        .payment-info h3, .notes-section h3 {
          font-size: 14px;
          font-weight: bold;
          margin-bottom: 10px;
          color: #333;
        }

        .payment-details p, .notes-section p {
          font-size: 12px;
          margin-bottom: 5px;
        }

        .footer {
          margin-top: 40px;
          padding-top: 20px;
          border-top: 1px solid #ddd;
          text-align: center;
        }

        .footer-content p {
          margin-bottom: 5px;
          font-size: 11px;
        }

        .footer-small {
          color: #666;
          font-size: 10px;
        }

        .batch-summary {
          margin-top: 50px;
          padding-top: 30px;
          border-top: 3px solid #00ADB5;
          page-break-before: always;
        }

        .batch-summary h2 {
          font-size: 20px;
          color: #00ADB5;
          margin-bottom: 20px;
          text-align: center;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 15px;
          max-width: 600px;
          margin: 0 auto;
        }

        .summary-item {
          display: flex;
          justify-content: space-between;
          padding: 10px;
          background: #f8f9fa;
          border-radius: 5px;
          font-size: 14px;
        }

        .summary-item label {
          font-weight: bold;
          color: #333;
        }

        .summary-item span {
          color: #00ADB5;
          font-weight: bold;
        }

        @media print {
          body { margin: 0; }
          .multiple-invoice-print { margin: 0; padding: 15px; }
          .page-break { page-break-before: always; }
        }
      `;
    }
  }
};
</script>

<style scoped>
.multiple-invoice-print {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background: white;
  font-family: Arial, sans-serif;
}

.print-header {
  text-align: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 3px solid #00ADB5;
}

.print-header h1 {
  font-size: 28px;
  color: #00ADB5;
  margin-bottom: 10px;
}

.batch-info {
  font-size: 14px;
  color: #666;
  font-weight: bold;
}

.invoice-page {
  margin-bottom: 40px;
  page-break-inside: avoid;
}

.page-break {
  page-break-before: always;
}

.invoice-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #00ADB5;
}

.company-name {
  font-size: 24px;
  font-weight: bold;
  color: #00ADB5;
  margin-bottom: 10px;
}

.company-address {
  font-size: 11px;
  color: #666;
  line-height: 1.3;
}

.invoice-title {
  text-align: right;
}

.invoice-title h2 {
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin-bottom: 5px;
}

.invoice-number {
  font-size: 16px;
  font-weight: bold;
  color: #00ADB5;
}

.invoice-details {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
}

.billing-info h3 {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 10px;
  color: #333;
}

.member-info p {
  margin-bottom: 5px;
  font-size: 12px;
}

.invoice-meta {
  text-align: right;
}

.meta-item {
  margin-bottom: 8px;
  font-size: 12px;
}

.meta-item label {
  font-weight: bold;
  margin-right: 10px;
}

.status-badge {
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
}

.status-pending { background: #ff9800; color: white; }
.status-paid { background: #4caf50; color: white; }
.status-overdue { background: #f44336; color: white; }
.status-cancelled { background: #9e9e9e; color: white; }

.service-details {
  margin-bottom: 30px;
}

.service-details h3 {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 10px;
  color: #333;
}

.service-description {
  background: #f8f9fa;
  padding: 15px;
  border-left: 4px solid #00ADB5;
  font-size: 12px;
  line-height: 1.5;
}

.amount-section {
  margin-bottom: 30px;
}

.amount-details {
  border-top: 1px solid #ddd;
  padding-top: 15px;
}

.amount-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 12px;
}

.amount-row.total {
  font-size: 16px;
  font-weight: bold;
  border-top: 2px solid #00ADB5;
  padding-top: 10px;
  margin-top: 10px;
}

.amount {
  font-weight: bold;
  color: #00ADB5;
}

.payment-info, .notes-section {
  margin-bottom: 20px;
}

.payment-info h3, .notes-section h3 {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 10px;
  color: #333;
}

.payment-details p, .notes-section p {
  font-size: 12px;
  margin-bottom: 5px;
}

.footer {
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid #ddd;
  text-align: center;
}

.footer-content p {
  margin-bottom: 5px;
  font-size: 11px;
}

.footer-small {
  color: #666;
  font-size: 10px;
}

.batch-summary {
  margin-top: 50px;
  padding-top: 30px;
  border-top: 3px solid #00ADB5;
  page-break-before: always;
}

.batch-summary h2 {
  font-size: 20px;
  color: #00ADB5;
  margin-bottom: 20px;
  text-align: center;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  max-width: 600px;
  margin: 0 auto;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 5px;
  font-size: 14px;
}

.summary-item label {
  font-weight: bold;
  color: #333;
}

.summary-item span {
  color: #00ADB5;
  font-weight: bold;
}
</style>




